import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Headphones, Send, CheckCircle,
  LogIn, LogOut, Clock, User, MessageSquare, AlertCircle,
  Smile, Paperclip, X, Download,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { agentService } from '../../../services/agentService';
import { authService } from '../../../services/authService';

const POLL_INTERVAL = 3000;

const RESOLUTION_STATUSES = [
  { value: 'SATISFIED',          labelKey: 'agentInbox.resolutionSatisfied' },
  { value: 'ESCALATED',          labelKey: 'agentInbox.resolutionEscalated' },
  { value: 'UNRESOLVED',         labelKey: 'agentInbox.resolutionUnresolved' },
  { value: 'CALLBACK_REQUESTED', labelKey: 'agentInbox.resolutionCallbackRequested' },
];

const EMOJIS = [
  '😀','😃','😄','😁','😆','😅','😂','🤣','😊','😇',
  '🙂','🙃','😉','😌','😍','🥰','😘','😗','😙','😚',
  '😋','😛','😝','😜','🤪','🤨','🧐','🤓','😎','🤩',
  '🥳','😏','😒','😞','😔','😟','😕','🙁','☹️','😣',
  '😫','😩','🥺','😢','😭','😤','😠','😡','🤬','🤯',
  '😳','🥵','🥶','😱','😨','😰','😥','😓','🤗','🤔',
  '🤭','🤫','🤥','😶','😐','😑','😬','🙄','😯','😲',
  '🥱','😴','😵','🤢','🤮','🤧','😷','🤒','🤕','🤑',
  '👋','✋','👌','✌️','🤞','👍','👎','👏','🙌','🙏',
  '❤️','🧡','💛','💚','💙','💜','🖤','💔','✨','🔥',
];

const LiveInbox = () => {
  const { t } = useTranslation();

  const companyRole = localStorage.getItem('companyRole');
  const isPortalAgent = companyRole === 'CHAT_AGENT' || companyRole === 'CHAT_SUPERVISOR';
  const portalUser = authService.getCurrentUser();

  const agentInfo = isPortalAgent
    ? {
        fullName: [portalUser?.firstName, portalUser?.lastName].filter(Boolean).join(' ') || 'Agent',
        role: companyRole === 'CHAT_SUPERVISOR' ? 'SUPERVISOR' : 'SUPPORT',
      }
    : null;

  // Inbox state
  const [pendingConvs, setPendingConvs] = useState([]);
  const [myConvs, setMyConvs] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [inboxError, setInboxError] = useState(null);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolveStatus, setResolveStatus] = useState('SATISFIED');
  const [resolveNote, setResolveNote] = useState('');

  // Emoji + image state
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const lastMessageIdRef = useRef(0);
  const prevPendingIdsRef = useRef(new Set());
  const messagesEndRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Close emoji picker on outside click
  useEffect(() => {
    const handler = (e) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const scrollToBottom = () =>
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  useEffect(() => { scrollToBottom(); }, [messages]);

  // Poll pending + my active conversations
  useEffect(() => {
    if (!isPortalAgent) return;

    const poll = async () => {
      try {
        const [fresh, mine] = await Promise.all([
          agentService.getPendingConversations(),
          agentService.getMyConversations(),
        ]);
        const freshList = Array.isArray(fresh) ? fresh : [];
        const newOnes = freshList.filter(c => !prevPendingIdsRef.current.has(c.sessionId));
        if (newOnes.length > 0 && Notification.permission === 'granted') {
          newOnes.forEach(c =>
            new Notification(t('chatbot.liveInbox'), {
              body: c.visitorName || c.visitorEmail || 'Anonymous visitor',
            })
          );
        }
        prevPendingIdsRef.current = new Set(freshList.map(c => c.sessionId));
        setPendingConvs(freshList);
        setMyConvs(Array.isArray(mine) ? mine : []);
      } catch (err) {
        setInboxError(err.message);
      }
    };

    poll();
    const id = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [isPortalAgent, t]);

  // Poll messages for active conversation
  const pollMessages = useCallback(async () => {
    if (!activeConv) return;
    try {
      const newMsgs = await agentService.getMessagesSince(activeConv.sessionId, lastMessageIdRef.current);
      if (newMsgs && newMsgs.length > 0) {
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const added = newMsgs.filter(m => !existingIds.has(m.id));
          if (added.length === 0) return prev;
          lastMessageIdRef.current = Math.max(...newMsgs.map(m => m.id));
          return [...prev, ...added];
        });
      }
    } catch {
      // ignore polling errors silently
    }
  }, [activeConv]);

  useEffect(() => {
    if (!activeConv || !isPortalAgent) return;
    pollMessages();
    const id = setInterval(pollMessages, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [activeConv, isPortalAgent, pollMessages]);

  const handleLogout = () => {
    authService.logout();
  };

  const handleResume = (conv) => {
    setActiveConv(conv);
    setMessages([]);
    lastMessageIdRef.current = 0;
  };

  const handlePickup = async (conv) => {
    try {
      await agentService.pickup(conv.sessionId);
      setPendingConvs(prev => prev.filter(c => c.sessionId !== conv.sessionId));
      const pickedConv = { ...conv, conversationMode: 'HUMAN_MODE' };
      setMyConvs(prev => [...prev, pickedConv]);
      setActiveConv(pickedConv);
      setMessages([]);
      lastMessageIdRef.current = 0;
    } catch (err) {
      setInboxError(err.message);
    }
  };

  const clearSelectedImage = () => {
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    setSelectedImage(null);
    setImagePreviewUrl(null);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    setSelectedImage(file);
    setImagePreviewUrl(URL.createObjectURL(file));
    e.target.value = '';
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    const text = replyText.trim();
    if (!text && !selectedImage) return;
    if (!activeConv) return;

    setSendingReply(true);
    const imageFile = selectedImage;
    setReplyText('');
    clearSelectedImage();
    setShowEmojiPicker(false);

    try {
      let mediaUrl = null;
      if (imageFile) {
        setUploadingImage(true);
        const result = await agentService.uploadImage(imageFile);
        mediaUrl = result.url;
      }
      await agentService.reply(activeConv.sessionId, text, mediaUrl);
      await pollMessages();
    } catch (err) {
      setInboxError(err.message);
      setReplyText(text);
    } finally {
      setSendingReply(false);
      setUploadingImage(false);
    }
  };

  const openResolveModal = () => {
    setResolveStatus('SATISFIED');
    setResolveNote('');
    setShowResolveModal(true);
  };

  const handleResolve = async () => {
    if (!activeConv) return;
    setResolving(true);
    try {
      await agentService.resolve(activeConv.sessionId, resolveStatus, resolveNote || null);
      setShowResolveModal(false);
      setMyConvs(prev => prev.filter(c => c.sessionId !== activeConv.sessionId));
      setActiveConv(null);
      setMessages([]);
    } catch {
      // keep modal open on error
    } finally {
      setResolving(false);
    }
  };

  const roleColor = (role) => {
    if (role === 'USER') return 'bg-gray-100 text-gray-800';
    if (role === 'AGENT') return 'bg-blue-100 text-blue-800';
    return 'bg-green-50 text-green-700 italic';
  };

  const roleName = (role) => {
    if (role === 'USER') return activeConv?.visitorName || activeConv?.visitorEmail || t('chatbot.conv_visitor');
    if (role === 'AGENT') return agentInfo?.fullName || t('chatbot.conv_agent');
    return t('chatbot.conv_bot');
  };

  // Not a portal agent — redirect to landing page login
  if (!isPortalAgent) {
    const landingUrl = process.env.REACT_APP_LANDING_URL || 'http://localhost:3000';
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 w-full max-w-sm p-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center">
              <Headphones className="w-7 h-7 text-primary-600" />
            </div>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">{t('chatbot.liveInbox')}</h1>
          <p className="text-sm text-gray-500 mb-6">{t('chatbot.agentLoginFromLanding')}</p>
          <a
            href={`${landingUrl}/login`}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
          >
            <LogIn className="w-4 h-4" />
            {t('chatbot.goToLogin')}
          </a>
        </div>
      </div>
    );
  }

  // Inbox screen
  return (
    <div className="flex flex-col h-full space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('chatbot.liveInbox')}</h1>
          {agentInfo && (
            <p className="text-sm text-gray-500 mt-0.5">
              {agentInfo.fullName} · {agentInfo.role === 'SUPERVISOR' ? t('agents.roleSupervisor') : t('agents.roleSupport')}
            </p>
          )}
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <LogOut className="w-4 h-4" />
          {t('nav.logout')}
        </button>
      </div>

      {inboxError && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {inboxError}
          <button onClick={() => setInboxError(null)} className="ml-auto text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {/* Two-panel layout */}
      <div className="flex gap-4 flex-1 min-h-0" style={{ height: 'calc(100vh - 220px)' }}>
        {/* Left: My Active + Pending conversations */}
        <div className="w-72 flex-shrink-0 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto">

            {/* My Active section */}
            <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2 bg-blue-50">
              <User className="w-4 h-4 text-blue-500" />
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{t('chatbot.myActiveConversations')}</span>
              {myConvs.length > 0 && (
                <span className="ml-auto bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full">
                  {myConvs.length}
                </span>
              )}
            </div>
            {myConvs.length === 0 ? (
              <div className="flex items-center justify-center h-14 text-gray-400 border-b border-gray-100">
                <p className="text-xs">{t('chatbot.noActiveConversations')}</p>
              </div>
            ) : (
              myConvs.map(conv => (
                <div
                  key={conv.sessionId}
                  onClick={() => handleResume(conv)}
                  className={`px-4 py-3 border-b border-gray-50 cursor-pointer hover:bg-blue-50 transition-colors ${
                    activeConv?.sessionId === conv.sessionId
                      ? 'bg-blue-50 border-l-4 border-l-blue-500'
                      : ''
                  }`}
                >
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {conv.visitorName || conv.visitorEmail || 'Anonymous'}
                  </p>
                  {conv.visitorEmail && conv.visitorName && (
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{conv.visitorEmail}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    {conv.messageCount} {t('chatbot.cb_messages')}
                  </p>
                </div>
              ))
            )}

            {/* Pending section */}
            <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2 bg-orange-50">
              <Clock className="w-4 h-4 text-orange-500" />
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{t('chatbot.pendingConversations')}</span>
              {pendingConvs.length > 0 && (
                <span className="ml-auto bg-orange-100 text-orange-700 text-xs font-bold px-2 py-0.5 rounded-full">
                  {pendingConvs.length}
                </span>
              )}
            </div>
            {pendingConvs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-20 text-gray-400">
                <MessageSquare className="w-8 h-8 mb-2 opacity-30" />
                <p className="text-xs">{t('chatbot.noPendingConversations')}</p>
              </div>
            ) : (
              pendingConvs.map(conv => (
                <div
                  key={conv.sessionId}
                  className="px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {conv.visitorName || conv.visitorEmail || 'Anonymous'}
                      </p>
                      {conv.handoverReason && (
                        <p className="text-xs text-gray-500 mt-0.5 truncate">{conv.handoverReason}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {conv.messageCount} {t('chatbot.cb_messages')}
                      </p>
                    </div>
                    <button
                      onClick={() => handlePickup(conv)}
                      className="flex-shrink-0 text-xs px-2.5 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                    >
                      {t('chatbot.pickUpConversation')}
                    </button>
                  </div>
                </div>
              ))
            )}

          </div>
        </div>

        {/* Right: Active conversation */}
        <div className="flex-1 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
          {!activeConv ? (
            <div className="flex flex-col items-center justify-center flex-1 text-gray-400">
              <User className="w-12 h-12 mb-3 opacity-20" />
              <p className="text-sm">{t('chatbot.noConversationSelected')}</p>
            </div>
          ) : (
            <>
              {/* Conversation header */}
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {activeConv.visitorName || activeConv.visitorEmail || 'Anonymous'}
                  </p>
                  <p className="text-xs text-gray-500">{activeConv.sessionId}</p>
                </div>
                <button
                  onClick={openResolveModal}
                  disabled={resolving}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50"
                >
                  <CheckCircle className="w-3.5 h-3.5" />
                  {resolving ? t('common.loading') : t('chatbot.resolveConversation')}
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.role === 'USER' ? 'justify-start' : 'justify-end'}`}
                  >
                    <div className={`max-w-[75%] rounded-xl px-4 py-2.5 text-sm ${roleColor(msg.role)}`}>
                      <p className="text-xs font-medium mb-1 opacity-70">{roleName(msg.role)}</p>
                      {msg.mediaUrl && (
                        <div className="mb-1">
                          <img
                            src={msg.mediaUrl}
                            alt="attachment"
                            className="rounded-lg max-w-full mb-1 cursor-pointer hover:opacity-90 transition-opacity"
                            style={{ maxHeight: 200 }}
                            onClick={() => setLightboxUrl(msg.mediaUrl)}
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                          <a
                            href={msg.mediaUrl}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            <Download className="w-3 h-3" />
                            {t('common.download')}
                          </a>
                        </div>
                      )}
                      {msg.content && <p className="leading-relaxed">{msg.content}</p>}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply box */}
              <div className="px-5 py-3 border-t border-gray-100">
                {/* Image preview */}
                {imagePreviewUrl && (
                  <div className="relative inline-block mb-2">
                    <img
                      src={imagePreviewUrl}
                      alt="preview"
                      className="h-20 rounded-lg border border-gray-200 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setLightboxUrl(imagePreviewUrl)}
                    />
                    <button
                      type="button"
                      onClick={clearSelectedImage}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gray-700 text-white rounded-full flex items-center justify-center hover:bg-gray-900 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}

                {/* Emoji picker */}
                {showEmojiPicker && (
                  <div
                    ref={emojiPickerRef}
                    className="mb-2 bg-white border border-gray-200 rounded-xl shadow-lg p-3"
                  >
                    <div className="grid grid-cols-10 gap-0.5 max-h-44 overflow-y-auto">
                      {EMOJIS.map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => setReplyText(prev => prev + emoji)}
                          className="text-xl leading-none p-1 rounded hover:bg-gray-100 transition-colors"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <form onSubmit={handleSendReply} className="flex items-center gap-2">
                  {/* Emoji toggle */}
                  <button
                    type="button"
                    onClick={() => setShowEmojiPicker(prev => !prev)}
                    className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                      showEmojiPicker
                        ? 'bg-yellow-100 text-yellow-600'
                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Smile className="w-4 h-4" />
                  </button>

                  {/* Image attachment */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    onChange={handleImageSelect}
                  />

                  <input
                    type="text"
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    placeholder={t('chatbot.typeReply')}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <button
                    type="submit"
                    disabled={sendingReply || uploadingImage || (!replyText.trim() && !selectedImage)}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-1.5 text-sm font-medium flex-shrink-0"
                  >
                    <Send className="w-3.5 h-3.5" />
                    {uploadingImage ? t('common.loading') : t('chatbot.sendReply')}
                  </button>
                </form>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            className="absolute top-4 right-4 text-white bg-black/40 rounded-full p-2 hover:bg-black/60 transition-colors"
            onClick={() => setLightboxUrl(null)}
          >
            <X className="w-5 h-5" />
          </button>
          <a
            href={lightboxUrl}
            download
            target="_blank"
            rel="noopener noreferrer"
            className="absolute top-4 right-16 text-white bg-black/40 rounded-full p-2 hover:bg-black/60 transition-colors"
            onClick={e => e.stopPropagation()}
          >
            <Download className="w-5 h-5" />
          </a>
          <img
            src={lightboxUrl}
            alt="full size"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}

      {showResolveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {t('agentInbox.resolveTitle')}
            </h3>

            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('agentInbox.resolutionStatus')}
            </label>
            <select
              value={resolveStatus}
              onChange={(e) => setResolveStatus(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4"
            >
              {RESOLUTION_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{t(s.labelKey)}</option>
              ))}
            </select>

            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('agentInbox.resolutionNote')} <span className="text-gray-400">({t('common.optional')})</span>
            </label>
            <textarea
              value={resolveNote}
              onChange={(e) => setResolveNote(e.target.value)}
              rows={3}
              placeholder={t('agentInbox.resolutionNotePlaceholder')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none mb-6"
            />

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowResolveModal(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleResolve}
                disabled={resolving}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {resolving ? t('common.saving') : t('agentInbox.confirmResolve')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveInbox;
